// Elements that can hold focus without being explicitly opted out of the tab
// order. `[tabindex="-1"]` is excluded on purpose: it marks a node as
// programmatically focusable only, which is exactly what a trap must skip.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function isHidden(element: HTMLElement): boolean {
  // `offsetParent` and `getBoundingClientRect` cannot be consulted here: they
  // are meaningless in a layout-less environment (jsdom), which is where the
  // trap is exercised. The attribute-level signals are what survive.
  return (
    element.hasAttribute('hidden') ||
    element.hasAttribute('inert') ||
    element.closest('[inert]') !== null ||
    element.closest('[aria-hidden="true"]') !== null
  );
}

/**
 * `tabindex="-1"` on ANY element, not only on one that is focusable solely
 * because of its tabindex.
 *
 * The selector list above cannot express this on its own: `[tabindex="-1"]` is
 * excluded from the last entry, but every other entry matches on tag or
 * attribute alone, so `<iframe tabindex="-1">` — or a `<button tabindex="-1">`,
 * or an `<a href tabindex="-1">` — came back focusable despite explicitly opting
 * out. Which is exactly backwards: the whole point of the attribute is to mark a
 * node programmatically focusable only, and a trap must skip those.
 *
 * The case that surfaced it (IMM-609): a `Modal` whose body is a sandboxed
 * report frame. Landing initial focus on the iframe hands every subsequent
 * keystroke to the framed document, and a `sandbox=""` document is unreachable
 * from here — so Escape never arrives at the parent's handler and the dialog
 * cannot be closed from the keyboard at all.
 */
function isProgrammaticOnly(element: HTMLElement): boolean {
  return element.getAttribute('tabindex') === '-1';
}

/**
 * The focusable descendants of `container`, in tab order, skipping anything
 * hidden, inert or explicitly removed from the tab order.
 */
export function getFocusableElements(container: HTMLElement): ReadonlyArray<HTMLElement> {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !isHidden(element) && !isProgrammaticOnly(element),
  );
}
